import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';

/**
 * Configuration de TanStack Query — mêmes réglages que `frontend/` (voir son
 * `api/query-client.ts` pour le détail des deux décisions : pas de retry sur les 4xx, et un
 * `staleTime` non nul pour ne pas relancer toutes les requêtes à chaque changement d'onglet).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,

      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }

        return failureCount < 2;
      },
    },

    mutations: {
      retry: false,
    },
  },
});

/**
 * Clés de cache centralisées.
 *
 * Les déclarer en un seul endroit évite les fautes de frappe silencieuses, qui se
 * manifestent par une invalidation qui « ne marche pas » sans aucune erreur visible.
 */
export const queryKeys = {
  currentUser: ['auth', 'me'] as const,

  tenants: (filters?: Record<string, unknown>) =>
    filters ? (['admin', 'tenants', filters] as const) : (['admin', 'tenants'] as const),
  tenant: (id: string) => ['admin', 'tenants', id] as const,

  plans: ['admin', 'plans'] as const,

  videoOverlayTemplates: ['admin', 'video-overlay-templates'] as const,

  templatesAdmin: ['admin', 'templates'] as const,
  templateAudit: (templateId: string) => ['admin', 'templates', templateId, 'audit'] as const,
  // HORS du préfixe ['admin', 'templates'] : toute mutation de template invalide ce préfixe,
  // et chaque aperçu invalidé relancerait un rendu Skottie côté serveur alors que le contenu
  // d'origine de l'animation n'a pas changé. Seul un redépôt de fichier l'invalide.
  templatePreview: (templateId: string) => ['admin', 'template-previews', templateId] as const,

  platformAdmins: ['admin', 'users'] as const,
  tenantUsers: (tenantId: string) => ['admin', 'tenants', tenantId, 'users'] as const,

  /**
   * Liste transverse des utilisateurs clients. Préfixe distinct de `tenantUsers`, qui est
   * imbriqué sous un tenant : une action lancée depuis l'un des deux écrans doit invalider
   * les DEUX, le même utilisateur pouvant être affiché aux deux endroits en même temps.
   */
  platformUsers: (filters?: Record<string, unknown>) =>
    filters ? (['admin', 'tenant-users', filters] as const) : (['admin', 'tenant-users'] as const),

  conversations: (filters?: Record<string, unknown>) =>
    filters ? (['admin', 'conversations', filters] as const) : (['admin', 'conversations'] as const),
  conversation: (tenantId: string) => ['admin', 'conversations', tenantId] as const,

  archivedConversations: (tenantId: string, filters?: Record<string, unknown>) =>
    filters
      ? (['admin', 'conversations', tenantId, 'archive', filters] as const)
      : (['admin', 'conversations', tenantId, 'archive'] as const),
  archivedConversation: (conversationId: string) =>
    ['admin', 'conversations', 'archive', conversationId] as const,

  archivedConversationsAcrossTenants: (filters?: Record<string, unknown>) =>
    filters
      ? (['admin', 'conversations', 'archive-all', filters] as const)
      : (['admin', 'conversations', 'archive-all'] as const),

  conversationAuditLog: (tenantId: string) => ['admin', 'conversations', tenantId, 'audit'] as const,
} as const;
